import {
  IncrementalSearchRegionTypeInput,
  RateAmenitiesMealType,
  SearchPropertyContentCategory,
  SearchPropertyContentInputFacility,
  SearchPropertyContentRatings,
  SearchPropertyContentSortField,
  SortDirection,
} from 'generated/graphql'
import {
  SearchHotelFilterInput,
  SearchHotelFormInput,
  SearchHotelFormKeywordTypes,
  SearchHotelSortInput,
  guestRatingFilterValues,
  hotelCategories,
  hotelFacilityTypes,
  mealTypes,
  regionTypes,
  searchHotelFormKeywordTypes,
  searchHotelStarRatings,
} from 'helper/searchHotelParams'
import { MakeFieldOptional } from 'types/makeFieldOptional'
import { HotelOccupancy } from 'types/occupancy'
import { QueryString } from 'types/queryString'
import { HotelQueryParamsMaster } from './_master'
import { QueryHelper } from './helper'

export const searchHotelSortFields: readonly SearchPropertyContentSortField[] = ['inclusivePrice', 'recommend'] as const
const sortDirections: readonly SortDirection[] = ['asc', 'desc'] as const

const isFiniteNumber = (value: number | undefined): value is number => value !== undefined && Number.isFinite(value)

const getNonNegativeNumber = (value: string | string[] | undefined): number | undefined => {
  const parsed = QueryHelper.getNumber(value)
  return isFiniteNumber(parsed) && parsed >= 0 ? parsed : undefined
}

const getGuestRating = (value: string | string[] | undefined): number | undefined => {
  const parsed = QueryHelper.getNumber(value)
  if (!isFiniteNumber(parsed)) return
  return guestRatingFilterValues.some(rating => Number(rating) === parsed) ? parsed : undefined
}

export type SearchHotelQueryParams = Pick<HotelQueryParamsMaster, 'ci' | 'co' | 'ocs'> & {
  kw: string // keyword
  kt: string // keyword type
  rid?: string // region id
  rt?: string // region type
  lat?: string // latitude 緯度
  lon?: string // longitude 軽度
  pid?: string // property content id
  sf?: string // sort field
  sd?: string // sort direction
  sr?: string | string[] // star ratings
  lp?: string // lower limit price 下限
  up?: string // upper limit price 上限
  cs?: string | string[] // categories
  mgr?: string // min guest ratings
  mt?: string // meal type
  fs?: string | string[] // hotel facilities
  cvd?: string // covid-19 prevention コロナ対策
}

export type SearchHotelQueryParsedParams = MakeFieldOptional<{
  keyword: string
  keywordType: SearchHotelFormKeywordTypes
  checkin: string
  checkout: string
  occupancies: HotelOccupancy[]
  regionId: string
  regionType: IncrementalSearchRegionTypeInput
  coordinates: {
    latitude: number
    longitude: number
  }
  propertyContentId: string
  sortField: SearchPropertyContentSortField
  sortDirection: SortDirection
  stars: SearchPropertyContentRatings[]
  minPrice: number
  maxPrice: number
  categories: SearchPropertyContentCategory[]
  facilities: SearchPropertyContentInputFacility[]
  covid19Prevention: boolean
  minGuestRatings: number
  mealType: RateAmenitiesMealType
}>

export type SearchHotelQueryBuildInput = SearchHotelFormInput & Partial<SearchHotelSortInput & SearchHotelFilterInput>

export const SearchHotelQuery = {
  build: (data: SearchHotelQueryBuildInput): SearchHotelQueryParams => {
    const params: SearchHotelQueryParams = {
      kw: data.keyword,
      kt: data.keywordType,
      ci: data.checkin,
      co: data.checkout,
      ocs: QueryHelper.buildOccupanciesParams(data.occupancies),
    }

    params.pid = data.propertyContentId
    params.rid = data.regionId
    params.rt = data.regionType

    if (data.coordinates) {
      params.lat = data.coordinates.latitude.toString()
      params.lon = data.coordinates.longitude.toString()
    }

    if (data.sortField && data.sortField !== 'distance') {
      params.sf = data.sortField
      params.sd = data.sortDirection
    }

    if (data.stars && data.stars.length > 0) params.sr = data.stars
    if (data.minPrice) params.lp = data.minPrice.toString()
    if (data.maxPrice) params.up = data.maxPrice.toString()
    if (data.categories && data.categories.length > 0) params.cs = data.categories
    if (data.facilities && data.facilities.length > 0) params.fs = data.facilities
    if (data.minGuestRatings) params.mgr = data.minGuestRatings.toString()
    if (data.mealType) params.mt = data.mealType
    if (data.covid19Prevention) params.cvd = QueryHelper.buildBoolean(data.covid19Prevention)

    return params
  },
  parse: (query: QueryString): SearchHotelQueryParsedParams => {
    const q = query as Partial<Record<keyof SearchHotelQueryParams, string | string[]>>
    const lat = QueryHelper.getNumber(q.lat)
    const lon = QueryHelper.getNumber(q.lon)

    return {
      keyword: QueryHelper.getStringOrFirst(q.kw),
      keywordType: QueryHelper.getEnumOrFirst(q.kt, searchHotelFormKeywordTypes),
      checkin: QueryHelper.getStringOrFirst(q.ci),
      checkout: QueryHelper.getStringOrFirst(q.co),
      occupancies: QueryHelper.getAsOccupancies(q.ocs),
      regionId: QueryHelper.getStringOrFirst(q.rid),
      regionType: QueryHelper.getEnumOrFirst(q.rt, regionTypes),
      coordinates: isFiniteNumber(lat) && isFiniteNumber(lon) ? { latitude: lat, longitude: lon } : undefined,
      propertyContentId: QueryHelper.getStringOrFirst(q.pid),
      sortField: QueryHelper.getEnumOrFirst(q.sf, searchHotelSortFields),
      sortDirection: QueryHelper.getEnumOrFirst(q.sd, sortDirections),
      stars: QueryHelper.getEnumArray(q.sr, searchHotelStarRatings),
      minPrice: getNonNegativeNumber(q.lp),
      maxPrice: getNonNegativeNumber(q.up),
      categories: QueryHelper.getEnumArray(q.cs, hotelCategories),
      facilities: QueryHelper.getEnumArray(q.fs, hotelFacilityTypes),
      minGuestRatings: getGuestRating(q.mgr),
      mealType: QueryHelper.getEnumOrFirst(q.mt, mealTypes),
      covid19Prevention: QueryHelper.getBoolean(q.cvd),
    }
  },
}
